/**
 * The "Send feedback" form: what it is about, what the visitor wants to say, an
 * optional address for a reply, and the lead form's honeypot. It stands in for
 * the conversation while it is open (ChatWidget hides the transcript and the
 * composer rather than unmounting them), and its first line says who reads it —
 * the Ema Answers team, not the business whose site this is — with a link to
 * our privacy notice (feedback.ts).
 *
 * Validation lives in feedback.ts; the POST, and what a failure says, belong to
 * the parent, which knows the tenant, the language and the page.
 */
import { useEffect, useId, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import {
  FEEDBACK_LIMITS,
  FEEDBACK_PRIVACY_URL,
  FEEDBACK_TYPES,
  validateFeedback,
  type FeedbackInput,
  type FeedbackType,
} from './feedback';
import { CheckIcon } from './icons';
import type { WidgetStrings } from './strings';
import { honeypotStyle, type Styles } from './theme';

const typeLabel = (ui: WidgetStrings, type: FeedbackType): string =>
  ({
    general: ui.feedbackTypeGeneral,
    feature: ui.feedbackTypeFeature,
    bug: ui.feedbackTypeBug,
    love: ui.feedbackTypeLove,
  })[type];

interface FeedbackFormProps {
  ui: WidgetStrings;
  styles: Styles;
  brandColor: string;
  /**
   * Sends the validated input. Resolves to an error message to show above the
   * buttons (the form stays open, its text kept, for a retry), or null once it
   * has gone — the form then thanks the visitor.
   */
  onSubmit: (input: FeedbackInput) => Promise<string | null>;
  /** Back to the conversation. */
  onClose: () => void;
}

export function FeedbackForm({ ui, styles, brandColor, onSubmit, onClose }: FeedbackFormProps) {
  const [type, setType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  useEffect(() => {
    messageRef.current?.focus();
  }, []);

  // Once it has gone the form is gone too; the one control left takes the focus.
  useEffect(() => {
    if (sent) backRef.current?.focus();
  }, [sent]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const v = validateFeedback({ type, message, email, website });
    if (!v.ok) {
      setError(
        v.reason === 'tooLong'
          ? ui.leadTooLong
          : v.field === 'email'
            ? ui.leadEmailInvalid
            : v.field === 'message'
              ? ui.feedbackMessageRequired
              : ui.feedbackError,
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const failure = await onSubmit(v.value);
      if (failure) setError(failure);
      else setSent(true);
    } finally {
      setBusy(false);
    }
  };

  const labelStyle: CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', margin: '12px 0 4px' };
  const selectedChip: CSSProperties = {
    ...styles.chip,
    background: styles.chipHoverBackground,
    border: `1.5px solid ${styles.accent}`,
  };
  const primary: CSSProperties = {
    ...styles.chip,
    background: brandColor,
    borderColor: brandColor,
    color: styles.ink,
    opacity: busy ? 0.7 : 1,
    cursor: busy ? 'default' : 'pointer',
  };

  if (sent) {
    return (
      <div style={{ ...styles.messages, alignItems: 'center', justifyContent: 'center', textAlign: 'center' }} data-pwe="feedback-sent">
        <span
          aria-hidden="true"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: styles.chipHoverBackground,
            color: styles.accent,
          }}
        >
          <CheckIcon size={20} />
        </span>
        <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1a1a2e' }}>{ui.feedbackThanks}</p>
        <button ref={backRef} type="button" onClick={onClose} style={styles.chip}>
          {ui.backToChat}
        </button>
      </div>
    );
  }

  return (
    // noValidate for the lead form's reason: the messages are the widget's own,
    // in the visitor's language, rather than the browser's bubble in its own.
    //
    // `display: block` over the transcript's scrolling box it borrows: in that
    // box's flex column the fields, which carry the composer's `flex: 1`,
    // stretched to fill the panel — a 300px email box on a phone.
    <form onSubmit={submit} style={{ ...styles.messages, display: 'block' }} aria-label={ui.sendFeedback} aria-busy={busy} noValidate data-pwe="feedback-form">
      {/* Who reads it, and where we say what we do with it: told before anyone
          types, because this goes to us and not to the business whose site
          the visitor is on. */}
      <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.5, color: '#4b5563' }} data-pwe="feedback-intro">
        {ui.feedbackIntro}{' '}
        <a href={FEEDBACK_PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: styles.accent, textDecoration: 'underline' }}>
          {ui.privacyLinkLabel}
        </a>
      </p>

      {/* Host pages style fieldsets and legends (Bootstrap floats a legend at
          1.5rem); every property a legend could inherit is pinned. The gap
          above sits on the fieldset: a legend's own top margin is not
          honoured the same way in every engine. */}
      <fieldset style={{ border: 0, padding: 0, margin: '12px 0 0', minWidth: 0 }}>
        <legend style={{ ...labelStyle, float: 'none', width: 'auto', padding: 0, margin: '0 0 6px' }}>{ui.feedbackTypeLabel}</legend>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {FEEDBACK_TYPES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setType(option)}
              aria-pressed={type === option}
              style={type === option ? selectedChip : styles.chip}
              data-pwe="feedback-type"
              data-type={option}
            >
              {typeLabel(ui, option)}
            </button>
          ))}
        </div>
      </fieldset>

      <label htmlFor={`${id}-message`} style={labelStyle}>{ui.feedbackMessage}</label>
      <textarea
        id={`${id}-message`}
        ref={messageRef}
        name="message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={ui.feedbackPlaceholder}
        maxLength={FEEDBACK_LIMITS.message}
        rows={5}
        required
        style={{ ...styles.formField, resize: 'vertical', minHeight: '96px' }}
      />

      <label htmlFor={`${id}-email`} style={labelStyle}>{ui.feedbackEmail}</label>
      <input
        id={`${id}-email`}
        name="email"
        type="email"
        inputMode="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        maxLength={FEEDBACK_LIMITS.email}
        autoComplete="email"
        style={styles.formField}
      />

      {/* Honeypot, as on the lead form (LeadForm.tsx says why it is not display:none). */}
      <div style={honeypotStyle} aria-hidden="true">
        <label htmlFor={`${id}-website`}>Website</label>
        <input
          id={`${id}-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      {error && (
        <p role="alert" style={{ ...styles.formError, marginTop: '8px' }}>
          {error}
        </p>
      )}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
        <button type="button" onClick={onClose} style={styles.chip} disabled={busy}>
          {ui.leadCancel}
        </button>
        <button type="submit" style={primary} disabled={busy}>
          {busy ? ui.leadSending : ui.feedbackSubmit}
        </button>
      </div>
    </form>
  );
}
